import { useState, useEffect, useCallback, useRef } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { ErrorStackDataType, MessageDisplayType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import {
    Account, useAppSelector, useAppDispatch, ErrorStackItem, MessageHandlerDisplayType, getEnumKey, DriveErrorMessages, SearchNode,
    DirectoryEntity, DirectoryPaginationInfo, useFetchLink, DriveEntryType, useSearchSnapshotNodes, CollectionEntity, COLLECTION_PROTOCOL_VERSIONS,
    Message, SnapshotData, SearchClient, SearchProgressValues, SearchErrorCodes, DriveSearchLimits, SearchNodeBase
} from "src/common";
import { uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

enum RESET_STEPS {
    VALID,
    INVALID_REACHED_LIMIT,
    CLEARING_DB
}

export function useDriveSearch(current_account: Account, collection_info: CollectionEntity, search_client: SearchClient, search_term: string) {
    const root_info = useAppSelector((state: RootState) => state.drive.root_info);
    const [progress, setProgress] = useState<number>(SearchProgressValues.initial);
    const [loading, setLoading] = useState<boolean>(false);
    const [paused, setPause] = useState<boolean>(false);
    const [error, setError] = useState<SearchErrorCodes | undefined>();
    const { fetchSnapshot, destroySnapshot, snapshot_data } = useSearchSnapshotNodes(current_account, search_client);
    const { fetchLink, resetLink, directory_id, link_entity, directory_entity, error: link_error } = useFetchLink(current_account);
    const dispatch = useAppDispatch();
    const node_queue_total_ref = useRef<number>(0); // Total number of Links to be fetched
    const node_queue_ref = useRef<SearchNodeBase[]>([]);
    const working_node_ref = useRef<SearchNodeBase>();
    const reset_step = useRef<RESET_STEPS>(RESET_STEPS.VALID);
    const reset_total_ref = useRef<number>(0);
    // Description: Hold progress in ref to dynamically check within functions
    const progress_ref = useRef<number>(progress);
    function setProgressRef(_progress: number) {
        progress_ref.current = _progress;
        setProgress(_progress);
    }

    const backups_ref = useRef<SearchNode[] | undefined>();
    // Description: get all the results from DB
    const results = useLiveQuery(
        async () => {
            if (!!search_term) {
                const _results = await search_client.db.nodes
                    .where("name_tokens")
                    .startsWithIgnoreCase(search_term)
                    .limit(DriveSearchLimits.LIVE_QUERY_LIMIT)
                    .toArray()
                    .then((_result: SearchNode[]) => _.uniqBy(_result, "id"));
                if (reset_step.current === RESET_STEPS.CLEARING_DB && !_results.length) {
                    const _backup = backups_ref.current?.slice();
                    initResetSteps();
                    return _backup;
                }
                return _results;
            } else {
                return undefined;
            }
        }, [search_term]  // NOTE: specify vars that affect query:
    );

    // Description: Handle result set limits and reset steps
    useEffect(() => {
        // NOTE: If Search reaches LIVE_QUERY_LIMIT, pause the queue
        if (!!results && results.length >= DriveSearchLimits.LIVE_QUERY_LIMIT) {
            setPause(true);
        } else if (reset_step.current === RESET_STEPS.INVALID_REACHED_LIMIT && !!working_node_ref.current) {
            reset_step.current = RESET_STEPS.CLEARING_DB;
            backups_ref.current = results?.slice();
            handleIndexedDBNodeLimit(working_node_ref.current, backups_ref.current);
        };
    }, [results]);

    // Description: Handle the new directory fetched - handle one at the time by type 
    useEffect(() => {
        if (!!directory_id) {
            !!working_node_ref.current ?
                fetchSnapshot(working_node_ref.current.linked_collection_id, directory_id, link_entity?.getCollectionProtocolVersion(), working_node_ref.current.sequences) :
                handleItemError(`node_id: ${directory_id}`, DriveErrorMessages.default, {
                    stack_message: "No working_node_ref found when a new directory_id was returned",
                    stack_error: { directory_id }
                });
            resetLink();
        }

        // Description: Handle the link errors
        if (link_error) {
            handleItemError(`node_id: ${directory_id}`, DriveErrorMessages.default, {
                stack_message: "NO working_node_ref found when a new directory_id was returned",
            });
        }
    }, [directory_id, link_error]);

    useEffect(() => {
        if (!!directory_entity) {
            // NOTE: If this is an effective root then call snapshot without an id (no root id)
            !!working_node_ref.current ?
                fetchSnapshot(working_node_ref.current.linked_collection_id, "", COLLECTION_PROTOCOL_VERSIONS.V2, working_node_ref.current.sequences) :
                handleItemError(`node_id: ${directory_entity.id}`, DriveErrorMessages.default, {
                    stack_message: "NO working_node_ref found when a new directory entity was returned",
                    stack_error: directory_entity
                });
            resetLink();
        }
    }, [directory_entity]);

    // Description: Handle Snapshot data returned Entries
    useEffect(() => {
        !!snapshot_data && handleSnapshotData(snapshot_data);
    }, [snapshot_data]);

    // ----------------------------------------------------------------------------------------------------
    // Description: Handles Fetching, storing, manipulating data, permissions, and grants
    // ----------------------------------------------------------------------------------------------------
    // Description: Add current directory entries to Indexed DB
    // NOTES: Might need to get current sequence because upload does not update pagination_info
    async function handleAddCurrentDirectoryNodes(working_directory: DirectoryEntity) { // TODO: pagination_info?: DirectoryPaginationInfo
        let nodes_map;
        // NOTE: Save the  nodes to db and get SearchNodeMapTypes for iterations
        if (working_directory.entries.length > 0) {
            // NOTE: set the number of entries to find for progress
            node_queue_total_ref.current = working_directory.entries.length;
            nodes_map = SearchNode.parseDirectoryToSearchNodes(working_directory);
            saveSearchNodes(nodes_map.search_nodes, working_directory.id, SearchProgressValues.currentNodes);
        }
        // Description: Set up the queue and begin the vertical iteration of the tree
        !!nodes_map?.folder_nodes && _.forEach(nodes_map.folder_nodes, (node: SearchNode) => node_queue_ref.current.push(node.item));
        getNext();
    }

    // Description: Handle Snapshot data and add LINKS to QUEUE if any
    async function handleSnapshotData(_snapshot_data: SnapshotData) {
        let move_next = false;
        // NOTE: Handle Additional Links in the snapshot
        if (_snapshot_data.links && _snapshot_data.links.length > 0) {
            node_queue_total_ref.current = (node_queue_total_ref.current + Number(_snapshot_data.links.length));
            // NOTE: Add a stub of the links to the QUEUE as a search_node. 
            // The actual entries will be added from the previous snapshots - the children will be fetched getNext
            _.forEach(_snapshot_data.links, (link: FSMessage.Link) => {
                const search_node = SearchNode.parseLinksToSearchNodes(link, root_info?.collection_id);
                node_queue_ref.current.unshift(search_node.item); // NOTE: Add to the top of the queue
            });
        }

        // NOTE: Handle Additional snapshot pages by attaching them to the end of the queue
        if (!!_snapshot_data.sequences || !_snapshot_data.has_more) {
            if (!!working_node_ref.current && !!_snapshot_data.sequences && _snapshot_data.sequences[0] > 0 && _snapshot_data.sequences[1] > 0) {
                node_queue_total_ref.current++;
                const new_node = Object.assign({}, working_node_ref.current, { sequences: _snapshot_data.sequences });
                node_queue_ref.current.push(new_node);
            }
            move_next = true;
        }

        // NOTE: Handle SearchNodes
        (_snapshot_data.search_nodes && _snapshot_data.search_nodes.length > 0) &&
            saveSearchNodes(_snapshot_data.search_nodes, working_node_ref.current?.id);

        // NOTE: only move on to next if the snapshot is finished OR the current pagination batch has been fetched
        move_next && getNext();
    }

    // ----------------------------------------------------------------------------------------------------
    // Description: Handle Queue and progress and updates, build data
    // ----------------------------------------------------------------------------------------------------
    // Description: Move to next item in queue
    async function getNext() {
        if (!paused) {
            if (node_queue_ref.current.length > 0) {
                const valid = await validateDBLimits();
                working_node_ref.current = node_queue_ref.current.shift();
                if (!!working_node_ref.current) {
                    if (valid) {
                        handleWorkingNode(working_node_ref.current);
                    } else {
                        reset_step.current = RESET_STEPS.INVALID_REACHED_LIMIT;
                    }
                } else {
                    handlePageErrorMessage("Stop the process: working_node_ref.current is undefined");
                }
            } else {
                handleSuccess();
            }
        }
    }

    // Description: Validate DB has not reached  INDEXED_DB_NODE_LIMITS
    async function validateDBLimits(): Promise<boolean> {
        const node_rows_count = await search_client.getSearchNodeTotalRowCounts();
        return node_rows_count < DriveSearchLimits.INDEXED_DB_NODE_LIMITS;
    }

    // Description: Re-start the reset steps to valid 
    function initResetSteps() {
        reset_step.current = RESET_STEPS.VALID;
        backups_ref.current = undefined;
    }

    // Description: If the INDEXED_DB_NODE_LIMITS has been exceeded, we need to reset the db, 
    // keep current results and continue searching the rest of the queue
    async function handleIndexedDBNodeLimit(working_node: SearchNodeBase, saved_results: SearchNode[] = []) {
        reset_total_ref.current++;
        // NOTE: Remove data from Node table to refill and keep search table to track
        await search_client.destroyNodeTableSelective(saved_results);
        handleWorkingNode(working_node);
    }

    // Description: Process next queue item / working node according to type
    function handleWorkingNode(working_node: SearchNodeBase) {
        // NOTE: get the new directory or link / root directory info
        working_node.type === DriveEntryType.LINK ? fetchLink(working_node.collection_id, working_node.id) :
            working_node.type === DriveEntryType.DIR ? fetchSnapshot(working_node.collection_id, working_node.id, collection_info.collection_protocol_version, working_node.sequences) :
                handleItemError(working_node.name, DriveErrorMessages.default, {
                    stack_message: "SEARCH NODE TYPE is not DIR or LINK"
                });
    }

    // Description: Save nodes to db or store
    function saveSearchNodes(search_nodes: SearchNode[], id: string = "", _progress?: number) {
        handleProgress(_progress);
        search_client.putBulkItemsToIndex(search_nodes);
    }

    // Description: Set Dynamic progress from a min and a max range
    function handleProgress(_progress?: number) {
        if (!!_progress) {
            setProgressRef(_progress);
        } else {
            const min = SearchProgressValues.currentNodes;
            const max = SearchProgressValues.fetchingOldSnapshotPages;
            const total = node_queue_total_ref.current;
            const counts = node_queue_ref.current.length;
            const calc_dynamic_progress = max - Math.round(counts / total * (max - min));
            // NOTE: Only increase progress if its greater than already saved
            if (calc_dynamic_progress >= min && calc_dynamic_progress <= max && calc_dynamic_progress >= progress_ref.current) {
                setProgressRef(calc_dynamic_progress);
            }
        }
    }

    // Description: Wrap up the fetch and build - complete db search
    async function handleSuccess() {
        setProgressRef(SearchProgressValues.complete);
        setLoading(false);
    }

    // Description: reset item on error and move to next
    function handleItemErrorReset(item_name: string) {
        working_node_ref.current = undefined;
        resetLink();
        getNext();
    }
    // ----------------------------------------------------------------------------------------------------
    // Description: Callbacks and general calls
    // ----------------------------------------------------------------------------------------------------
    // Description:  Handle queue item error
    function handleItemError(item_name: string, message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null) {
        const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
        const stack = new ErrorStackItem("[useDriveSearch Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), stack));
        handleItemErrorReset(item_name);
    }

    // Description: Handle error message and send error to store - pass error to parent to handle in the UI 
    function handlePageErrorMessage(message: string, error_code: SearchErrorCodes = SearchErrorCodes.DEFAULT, stack_data?: ErrorStackDataType, display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
        const stack = new ErrorStackItem("[useDriveSearch Hook]", message, "error", stack_data).info;
        dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
        setPause(true);
        setError(error_code);
        // NOTE: Needs to reset the ref for the queue because it becomes read-only and throws an error on getNext
        node_queue_ref.current = node_queue_ref.current.slice();
    }

    // Description: Reset all state for internal and external clean up
    function reset(_progress: number = SearchProgressValues.initial) {
        node_queue_total_ref.current = 0;
        node_queue_ref.current = [];
        working_node_ref.current = undefined;
        destroySnapshot();
        resetLink();
        setProgressRef(_progress);
        setLoading(false);
        setPause(false);
        setError(undefined);
        initResetSteps();
        if (reset_total_ref.current > 0) {
            search_client.destroySearch();
            reset_total_ref.current = 0;
        }
    }

    // Description: Reset Search Hook from Component
    const resetSearch = useCallback(() => {
        reset();
    }, []);

    // Description: Initialize or update the search db
    const initSearch = useCallback(async (current_directory: DirectoryEntity, pagination_info?: DirectoryPaginationInfo) => {
        setLoading(true);
        setProgressRef(SearchProgressValues.loading);
        // NOTE: Check if the current directory is the same as last searched
        const identifiers = await search_client.getCurrentSearch();
        if (!!identifiers && (identifiers.collection_id !== current_directory.collection_id || identifiers.id !== current_directory.id)) {
            await search_client.destroySearch();
        }
        await search_client.setCurrentSearch({ collection_id: current_directory.collection_id, id: current_directory.id });
        handleAddCurrentDirectoryNodes(current_directory); // TODO: handle pagination_info - not added yet in current directory
    }, []);

    // Description: Pause the queue allow liveQuery to run when data is not fully loaded 
    //              If unpausing (_pause === false), then getNext in queue
    const pauseSearch = useCallback((_pause: boolean) => {
        setPause(_pause);
        !_pause && getNext();
    }, []);

    // Description: Development tools to remove the DB
    const destroySearch = useCallback(async () => {
        setPause(true); // NOTE: Pause until the db is fully deleted
        // await search_client.deleteUserDB(current_account.user_id);
        await search_client.destroySearch();
        reset();
    }, []);

    return {
        initSearch,
        pauseSearch,
        resetSearch,
        destroySearch,
        results,
        progress,
        loading, // NOTE: returns the state of fetching data 
        paused, //  NOTE: paused fetching data - queue is not finished 
        error
    };
}
